import { useDomainCRUDActions } from '@mm/state/actions/DomainCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { domainSelector } from '@mm/state/selectors/domainSelector';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ArrayItemType } from '@mm/utils';
import { ReactComponent as WarningIcon } from 'assets/warning.svg';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getConnectionStatus } from '../../../../configure/panels/sections/MaintenanceSettingsSection/FacebookConnectionStatus/FacebookConnectionStatus';
import { ChatWidgetInteractiveDescriptions } from './ChatWidgetInteractiveDescriptions';
import { ChatWidgetInteractiveDescriptionsLeft } from './ChatWidgetInteractiveDescriptionsLeft';
import './ChatWidgetPreviewFrame.pcss';
import { ChatWidgetPreviewMessengerIFrame } from './ChatWidgetPreviewMessengerIFrame';
import { ChatWidgetPreviewMessengerScreenshotFrame } from './ChatWidgetPreviewMessengerScreenshotFrame';
import { ChatWidgetPreviewWebchatIFrame } from './ChatWidgetPreviewWebchatIFrame';
import { ChatWidgetStaticDescriptions } from './ChatWidgetStaticDescriptions';
import { ChatWidgetStaticDescriptionsLeft } from './ChatWidgetStaticDescriptionsLeft';

export const ChatWidgetPreviewChannelOptions = [
  'web_chat',
  'facebook',
] as const;
export type ChatWidgetPreviewChannelOption = ArrayItemType<
  typeof ChatWidgetPreviewChannelOptions
>;
export const ChatWidgetPreviewChannelOptionLabels: Record<
  ChatWidgetPreviewChannelOption,
  string
> = {
  facebook: 'Messenger',
  web_chat: 'Webchat',
};

type ChatWidgetPreviewFrameProps = {
  channel: ChatWidgetPreviewChannelOption;
  className: string;
  chatWidget: PromoterModel;
  showGreeting?: boolean | 'without_delay';
  interactive?: boolean;
  includeDescriptions?: boolean;
};

export type ChatWidgetPreviewIFrameProps = {
  className: string;
  chatWidget: PromoterModel;
  greetingWidget: any;
  showGreeting?: boolean | 'without_delay';
  onGreetingShown?: (bubbleShown?: boolean) => void;
};

export const ChatWidgetPreviewFrame = (props: ChatWidgetPreviewFrameProps) => {
  const {
    className,
    channel,
    chatWidget,
    showGreeting,
    interactive,
    includeDescriptions,
  } = props;

  const { show: fetchWidget } = useWidgetCRUDActions();
  const { update: updateDomains } = useDomainCRUDActions();
  const { setSuppressEmbeddedChatWidget } = useViewActions();

  const domain = useSelector(domainSelector);
  const { chatWidgetChannel } = useSelector(activeChatbotSelector);
  const widgets = useSelector((state: StoreState) => state.widget.entities);

  // Intentional `any` type here since it's a BE widget structure we're passing along verbatim
  const [greetingWidget, setGreetingWidget] = useState<any>();
  const [widgetBubbleShowing, setWidgetBubbleShowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const activePage = useSelector(activeFacebookPageSelector);
  const [connectionStatus] = getConnectionStatus(activePage);

  useEffect(() => {
    setWidgetBubbleShowing(false);
    setLoading(true);
    const timeout = setTimeout(() => setLoading(false), 10000);
    return () => clearTimeout(timeout);
  }, [channel, chatWidget]);

  const onGreetingShown = (bubbleShown: boolean) => {
    setWidgetBubbleShowing(bubbleShown);
    setLoading(false);
  };

  useEffect(() => {
    setSuppressEmbeddedChatWidget(true);
    return () => {
      setSuppressEmbeddedChatWidget(false);
    };
  }, [setSuppressEmbeddedChatWidget]);

  const greetWithStartbotEnabled =
    (channel === 'facebook' && chatWidget.greetWithStartbot) ||
    (channel === 'web_chat' && chatWidget.webchatGreetWithStartbot);

  const firstDialogueWidget = useMemo(() => {
    if (widgets.length === 0 || !greetWithStartbotEnabled) {
      return;
    }
    const dialogueWidgets = widgets
      .filter(w => w.pageId === chatWidget.pageId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    return dialogueWidgets[0];
  }, [widgets, chatWidget, greetWithStartbotEnabled]);

  const firstDialogWidgetCompatible = ['text', 'quick_question'].includes(
    firstDialogueWidget?.type || '',
  );

  useEffect(() => {
    if (!firstDialogueWidget || !firstDialogWidgetCompatible) {
      setGreetingWidget(undefined);
      return;
    }
    const fetchGreetingWidget = async () => {
      const firstWidgetWithStructure = await fetchWidget(
        firstDialogueWidget.id,
        true,
      );
      if (!firstWidgetWithStructure) {
        setGreetingWidget(undefined);
        return;
      }
      setGreetingWidget(firstWidgetWithStructure.structure);
    };

    fetchGreetingWidget();
  }, [firstDialogueWidget, firstDialogWidgetCompatible, fetchWidget]);

  useEffect(() => {
    if (domain.domainsList === undefined) {
      return;
    }
    const thisSiteDomain = window.location.origin;
    const whitelisted = domain.domainsList.find(
      d => d === thisSiteDomain || d === `${thisSiteDomain}/`,
    );
    if (!whitelisted && activePage && connectionStatus === 'OK') {
      updateDomains({
        id: domain.id,
        connectedPageId: activePage.id,
        domainsList: [...domain.domainsList, thisSiteDomain],
      });
    }
  }, []);

  const channelDisabled =
    ['facebook', 'web_chat'].includes(chatWidgetChannel || '') &&
    channel !== chatWidgetChannel;

  const content = () => {
    if (channelDisabled) {
      return (
        <div className="chat-widget-preview-frame__channel-disabled-container">
          <div className="chat-widget-preview-frame__channel-disabled">
            <WarningIcon />
            &nbsp;&nbsp;Chat widget set to{' '}
            {chatWidgetChannel === 'web_chat' ? 'Webchat' : 'Messenger'} only
          </div>
        </div>
      );
    }

    const iframeProps: ChatWidgetPreviewIFrameProps = {
      className: classNames('chat-widget-preview-frame__widget-iframe', {
        'chat-widget-preview-frame__widget-iframe--non-interactive':
          !interactive,
      }),
      chatWidget,
      greetingWidget,
      showGreeting,
      onGreetingShown,
    };
    switch (channel) {
      case 'facebook':
        if (interactive) {
          return <ChatWidgetPreviewMessengerIFrame {...iframeProps} />;
        }
        return <ChatWidgetPreviewMessengerScreenshotFrame {...iframeProps} />;
      case 'web_chat':
        return <ChatWidgetPreviewWebchatIFrame {...iframeProps} />;
      default:
        return null;
    }
  };

  const ChatWidgetDescription = () => {
    if (
      !includeDescriptions ||
      channel !== 'web_chat' ||
      !widgetBubbleShowing ||
      loading
    ) {
      return null;
    }
    switch (chatWidget.greetingStyle) {
      case 'inline_pic':
        return chatWidget.position === 'bottom_left' ? (
          <ChatWidgetInteractiveDescriptionsLeft className="chat-widget-preview-frame__description-inline-pic-left" />
        ) : (
          <ChatWidgetInteractiveDescriptions className="chat-widget-preview-frame__description-inline-pic" />
        );
      case 'static':
        return chatWidget.position === 'bottom_left' ? (
          <ChatWidgetStaticDescriptionsLeft className="chat-widget-preview-frame__description-static-left" />
        ) : (
          <ChatWidgetStaticDescriptions className="chat-widget-preview-frame__description-static" />
        );
      default:
        return null;
    }
  };

  return (
    <div className={classNames('chat-widget-preview-frame', className)}>
      {content()}
      {loading && !channelDisabled && (
        <div className="chat-widget-preview-frame__loading-notifier">
          <i className="fas fa-spinner fa-spin" /> Loading preview...
        </div>
      )}
      <ChatWidgetDescription />
    </div>
  );
};
